import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordStrength, passwordScore } from "@/components/PasswordStrength";
import { useMeta } from "@/lib/useMeta";

const NAVY = "#0A1628";
const CARD = "#112240";
const GOLD = "#C9A84C";
const fieldStyle = { background: "rgba(255,255,255,0.04)", borderColor: "rgba(201,168,76,0.22)", color: "#EAF0F7" };

export default function ResetPassword() {
  useMeta({ title: "Set a new password | Plan-Sarthi", description: "Choose a new Plan-Sarthi password.", path: "/reset-password" });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const score = useMemo(() => passwordScore(password), [password]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords do not match");
    if (score.met < 4) return toast.error("Password does not meet the required rules");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated");
      navigate("/login?reset=1");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-12" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="reset-password-page">
      <Link to="/" className="flex items-baseline mb-8">
        <span className="text-2xl font-bold tracking-tight text-white">Plan</span>
        <span className="text-2xl font-bold tracking-tight" style={{ color: GOLD }}>-</span>
        <span className="text-2xl font-bold tracking-tight text-white">Sarthi</span>
      </Link>
      <div className="w-full max-w-[420px] border p-7 sm:p-9" style={{ background: CARD, borderColor: "rgba(201,168,76,0.18)" }}>
        <h1 className="text-white text-2xl font-semibold tracking-tight">Set a new password</h1>
        {!token ? (
          <p className="text-[13px] mt-3" style={{ color: "#FCA5A5" }} data-testid="reset-token-missing">
            This reset link is incomplete. Request a new one from{" "}
            <Link to="/forgot-password" className="underline" style={{ color: GOLD }}>Forgot Password</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(234,240,247,0.6)" }}>New password</Label>
              <div className="relative">
                <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required style={{ ...fieldStyle, paddingRight: "2.5rem" }} data-testid="reset-password-input" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity duration-150" style={{ color: "#EAF0F7" }} aria-label="Toggle password" data-testid="reset-toggle-visibility">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrength password={password} dark />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(234,240,247,0.6)" }}>Confirm password</Label>
              <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={fieldStyle} data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" className="w-full font-semibold" style={{ background: GOLD, color: NAVY }} disabled={busy} data-testid="reset-submit-btn">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
